import { useEffect, useSyncExternalStore } from 'react';
import { canAction, getSidebarSections } from '../menu';
import { ensureProjectMenu, getProjectMenuVersion, subscribeProjectMenu } from '../projectMenu';
import GlobalSearch from './GlobalSearch';
import NexoraLogo from './NexoraLogo';
import { createLayout } from './kernelLayout';
import { kernelIcons } from './kernelIcons';

const icons = {
  ...kernelIcons,
  ticket: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 9a3 3 0 0 1 0 6v2a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-2a3 3 0 0 1 0-6V7a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2z" />
      <line x1="9" y1="5" x2="9" y2="19" strokeDasharray="2 2" />
    </svg>
  ),
  mic: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="9" y="2" width="6" height="12" rx="3" />
      <path d="M5 11a7 7 0 0 0 14 0" />
      <line x1="12" y1="18" x2="12" y2="22" />
    </svg>
  ),
  folder: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
    </svg>
  ),
  tag: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20.59 13.41 11 3.83A2 2 0 0 0 9.59 3.24L4 3a1 1 0 0 0-1 1l.24 5.59a2 2 0 0 0 .59 1.41l9.58 9.58a2 2 0 0 0 2.83 0l4.35-4.35a2 2 0 0 0 0-2.82z" />
      <circle cx="8.5" cy="8.5" r="1.5" />
    </svg>
  ),
  briefcase: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  ),
  user: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  layout: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="9" y1="21" x2="9" y2="9" />
    </svg>
  ),
  table: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="3" x2="9" y2="21" />
    </svg>
  ),
  zap: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  ),
  chat: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  ),
  sparkle: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3z" /><path d="M19 15l.7 1.8 1.8.7-1.8.7L19 20l-.7-1.8-1.8-.7 1.8-.7L19 15z" />
    </svg>
  ),
};

const brand = {
  mode: 'svg',
  mark: <NexoraLogo />,
};

// React Router non ri-monta la pagina se si clicca un link verso la rotta
// già attiva (nessun cambio di location) — quindi uno stato locale come
// "form di modifica aperto" resta visibile anche dopo aver ricliccato la
// stessa voce di menu, dando l'impressione che il click non abbia fatto
// nulla. In quel caso specifico forziamo un reload pulito della pagina.
const createItemClickHandler = ({ closeMenu, location }) => (item) => (e) => {
  closeMenu();
  if (item.path === location.pathname) {
    e.preventDefault();
    window.location.reload();
  }
};

// La galleria Modelli non ha una voce di menu: si apre da Progetti, che resta evidenziata.
const resolveActive = (item, location) => (
  item.key === 'projects' && location.pathname === '/project-templates'
    ? true
    : item.end ? location.pathname === item.path : location.pathname.startsWith(item.path)
);

// Le voci dei progetti usano l'emoji del modello come icona ("emoji:🎯").
const iconsWithEmoji = new Proxy(icons, {
  get: (target, key) => (
    typeof key === 'string' && key.startsWith('emoji:')
      ? <span className="nav-emoji" aria-hidden="true">{key.slice(6)}</span>
      : target[key]
  ),
});

const KernelLayout = createLayout({
  icons: iconsWithEmoji,
  brand,
  getVisibleSections: getSidebarSections,
  resolveActive,
  createItemClickHandler,
});

// Si ridisegna quando cambia l'elenco dei progetti (creazione, rinomina, eliminazione).
function Layout(props) {
  useSyncExternalStore(subscribeProjectMenu, getProjectMenuVersion);
  const { user } = props;
  useEffect(() => {
    if (user && canAction(user, 'projects.manage')) ensureProjectMenu(user.id);
  }, [user]);
  return (
    <>
      <KernelLayout {...props} />
      {user && canAction(user, 'projects.manage') && <GlobalSearch />}
    </>
  );
}

export default Layout;
